import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import PasswordChecklist from "react-password-checklist";

import AuthService from "../services/auth.service";
import { useAppContext } from "../AppContext";
import { MODAL_TYPES } from '../modals/modalTypes';
import LanguageSwitcher from "./LanguageSwitcher";

const getTermsContent = (language) => {
  if (language === 'nl') {
    return `
#### 1. Definities
**1.1. Dienst**  
De software as a service (SaaS) oplossing die wordt geleverd door Risqui bv (hierna "Risqui"), inclusief alle gerelateerde diensten, functies en functionaliteiten.

**1.2. Klant**  
De rechtspersoon of natuurlijke persoon die een overeenkomst aangaat met Risqui om de dienst te gebruiken.

**1.3. Gebruiker**  
Elke persoon die door de klant is geautoriseerd om de dienst te gebruiken.

**1.4. Overeenkomst**  
Het contract tussen Risqui en de klant, inclusief deze algemene voorwaarden.

#### 2. Toepasselijkheid
**2.1. Algemene Voorwaarden**  
Deze algemene voorwaarden zijn van toepassing op alle aanbiedingen, overeenkomsten en leveringen met betrekking tot de dienst die door Risqui wordt geleverd.

**2.2. Afwijkingen**  
Eventuele afwijkingen van deze algemene voorwaarden zijn alleen geldig indien schriftelijk overeengekomen door beide partijen.

#### 3. Levering van de Dienst
**3.1. Dienst**  
Risqui levert de dienst op "as-is" basis zonder enige garanties, behalve zoals expliciet vermeld in deze algemene voorwaarden.

**3.2. Beschikbaarheid**  
Risqui zal redelijke inspanningen leveren om de beschikbaarheid en betrouwbaarheid van de dienst te waarborgen, maar garandeert geen ononderbroken of foutloze werking.

**3.3. Back-up**  
Risqui maakt regelmatig back-ups van de gegevens, maar dit is geen garantie. Aanvullende back-upvereisten moeten afzonderlijk worden overeengekomen.

#### 4. Informatiebeveiliging
Risqui is een beveiligingsbewust bedrijf; dit heeft een zeer hoge prioriteit.

**4.1. Algemeen**  
De aanbieder zal inspanningen leveren om de systemen up-to-date en veilig te houden, rekening houdend met de context van het systeem.

**4.2. Beveiligingstesten**  
Risqui is door een derde partij getest op kwetsbaarheden op het gebied van informatiebeveiliging. Gedetecteerde kwetsbaarheden worden zo snel mogelijk verholpen, rekening houdend met de ernst.

#### 5. Gebruik van de Dienst
**5.1. Rechtmatig Gebruik**  
De klant en gebruikers zijn verantwoordelijk voor het gebruik van de dienst in overeenstemming met toepasselijke wet- en regelgeving.

**5.2. Derden**  
De dienst mag niet worden gebruikt voor onwettige activiteiten of activiteiten die inbreuk maken op de rechten van derden.

#### 6. Vergoedingen en Betaling
De klant betaalt de vergoedingen zoals gespecificeerd in de overeenkomst. Risqui behoudt zich het recht voor om de vergoedingen jaarlijks aan te passen, mits de klant ten minste 30 dagen van tevoren wordt geïnformeerd.

#### 7. Intellectuele Eigendomsrechten
Alle intellectuele eigendomsrechten met betrekking tot de dienst blijven bij Risqui. De klant krijgt een niet-exclusief, niet-overdraagbaar recht om de dienst uitsluitend te gebruiken voor interne bedrijfsdoeleinden, in overeenstemming met de overeenkomst.

#### 8. Vertrouwelijkheid
Beide partijen bewaren de vertrouwelijkheid van alle vertrouwelijke informatie die tijdens de looptijd van de overeenkomst van de andere partij is ontvangen. Deze verplichting blijft bestaan na beëindiging van de overeenkomst.

#### 9. Beëindiging
De klant kan de overeenkomst beëindigen met een opzegtermijn van 30 dagen. Risqui kan de overeenkomst met onmiddellijke ingang beëindigen als de klant deze algemene voorwaarden schendt.

#### 10. Aansprakelijkheid
Risqui is alleen aansprakelijk voor directe schade als gevolg van opzet of grove nalatigheid. De aansprakelijkheid van Risqui is beperkt tot het bedrag dat door de klant is betaald in de twaalf maanden voorafgaand aan het incident dat aanleiding geeft tot de aansprakelijkheid.

#### 11. Overmacht
Risqui is niet aansprakelijk voor enige tekortkoming of vertraging in de uitvoering van haar verplichtingen als gevolg van omstandigheden buiten haar redelijke controle, waaronder maar niet beperkt tot natuurrampen, stakingen of internetstoringen (bijv. CrowdStrike).

#### 12. Aanvullende Overeenkomsten
Naast deze algemene voorwaarden kunnen aanvullende overeenkomsten worden gemaakt, die meer in detail specificeren welke dienst door Risqui aan de klant wordt geleverd. Dit moet door Risqui en de klant in een afzonderlijk document worden overeengekomen.

#### 13. Toepasselijk Recht en Geschillen
De overeenkomst en deze algemene voorwaarden worden beheerst door het Nederlands recht. Eventuele geschillen die voortvloeien uit of verband houden met de overeenkomst worden voorgelegd aan de bevoegde rechter in Utrecht, Nederland.

#### 14. Wijzigingen in de Algemene Voorwaarden
Risqui behoudt zich het recht voor om deze algemene voorwaarden te wijzigen. Vooral omdat Risqui een startend bedrijf is, wat betekent dat Risqui hard zal werken om meer dienstverlening aan klanten te bieden terwijl het bedrijf zich ontwikkelt. Dit moet worden weerspiegeld in deze algemene voorwaarden. Daarom kunnen en zullen de algemene voorwaarden veranderen. Klanten worden ten minste 30 dagen van tevoren geïnformeerd over eventuele wijzigingen. Als de klant niet akkoord gaat met de wijzigingen, kan hij de overeenkomst binnen 30 dagen na ontvangst van de kennisgeving beëindigen.`;
  }
  
  return `
#### 1. Definitions
**1.1. Service**  
The software as a service (SaaS) solution provided by Risqui bv (hereinafter "Risqui"), including any related services, features, and functionalities.

**1.2. Customer**  
The legal entity or individual who enters into an agreement with Risqui to use the service.

**1.3. User**  
Any individual authorized by the customer to use the service.

**1.4. Agreement**  
The contract between Risqui and the customer, including these terms and conditions.

#### 2. Applicability
**2.1. Terms and Conditions**  
These terms and conditions apply to all offers, agreements, and deliveries related to the service provided by Risqui.

**2.2. Deviations**  
Any deviations from these terms and conditions are only valid if agreed upon in writing by both parties.

#### 3. Provision of the Service
**3.1. Service**  
Risqui provides the service on an "as-is" basis without any warranties, except as explicitly stated in these terms and conditions.

**3.2. Availability**  
Risqui will make reasonable efforts to ensure the availability and reliability of the service but does not guarantee uninterrupted or error-free operation.

**3.3. Back-up**  
Risqui makes regular back-ups of the data, but this is no guarantee. Additional back-up requirements need to be agreed upon separately.

#### 4. Information Security
Risqui is a security-minded company; this has a very high priority.

**4.1. General**  
The provider will make efforts to keep the systems up-to-date and secure, considering the context of the system.

**4.2. Security Testing**  
Risqui has been tested for information security vulnerabilities by a third party. Detected vulnerabilities are fixed as soon as possible, taking into account the severity.

#### 5. Use of the Service
**5.1. Lawful Use**  
The customer and users are responsible for using the service in compliance with applicable laws and regulations.

**5.2. Third Parties**  
The service may not be used for any unlawful activities or activities that infringe on the rights of third parties.

#### 6. Fees and Payment
The customer shall pay the fees as specified in the agreement. Risqui reserves the right to adjust the fees every year, provided that the customer is informed at least 30 days in advance.

#### 7. Intellectual Property Rights
All intellectual property rights related to the service remain with Risqui. The customer is granted a non-exclusive, non-transferable right to use the service solely for its internal business purposes, in accordance with the agreement.

#### 8. Confidentiality
Both parties shall maintain the confidentiality of all confidential information received from the other party during the term of the agreement. This obligation shall continue even after the termination of the agreement.

#### 9. Termination
The customer may terminate the agreement with a notice period of 30 days. Risqui may terminate the agreement with immediate effect if the customer breaches these terms and conditions.

#### 10. Liability
Risqui is only liable for direct damages resulting from wilful misconduct or gross negligence. The liability of Risqui is limited to the amount paid by the customer in the twelve months preceding the incident giving rise to the liability.

#### 11. Force Majeure
Risqui is not liable for any failure or delay in the performance of its obligations due to circumstances beyond its reasonable control, including but not limited to natural disasters, strikes, or internet outages (e.g. CrowdStrike).

#### 12. Additional Agreements
On top of these terms and conditions, additional agreements can be made, which specify in more detail on the service that is provided by Risqui to the customer. This needs to be agreed upon by Risqui and the customer in a separate document.

#### 13. Applicable Law and Disputes
The agreement and these terms and conditions are governed by the laws of the Netherlands. Any disputes arising out of or in connection with the agreement shall be submitted to the competent court in Utrecht, the Netherlands.

#### 14. Amendments to the Terms and Conditions
Risqui reserves the right to amend these terms and conditions. Especially since Risqui is a starting company, meaning that Risqui will be working hard to provide more service towards customers while progressing. This has to be represented in these terms and conditions. Therefore, the terms and conditions can and will change. Customers will be informed of any changes at least 30 days in advance. If the customer does not agree with the amendments, they may terminate the agreement within 30 days of receiving the notice.`;
};

const CreateTrialEnvironment = () => {
  const { t, i18n } = useTranslation();
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordAgain, setPasswordAgain] = useState("");
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [projectName, setProjectName] = useState("");

  const [loading, setLoading] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(true);
  const [message, setMessage] = useState("");
  const [environmentUrl, setEnvironmentUrl] = useState(null);
  const [requestData, setRequestData] = useState(null);
  const [isKNMT, setIsKNMT] = useState(false);

  const [searchParams] = useSearchParams();
  const reqId = searchParams.get("id");
  const token = searchParams.get("token");

  let navigate = useNavigate();

  const { currentUser, openModal } = useAppContext();

  // Fetch request data on mount
  useEffect(() => {
    const fetchRequestData = async () => {
      if (!reqId || !token) {
        setLoadingRequest(false);
        return;
      }

      try {
        const data = await AuthService.getRequestData(reqId, token);
        setRequestData(data);
        
        // Check if this is a KNMT request
        if (data.source === 'KNMT') {
          setIsKNMT(true);
          // Pre-fill fields for KNMT users
          setName(data.name || '');
          setOrgName(data.practiceName || '');
          setProjectName(data.locationNames?.join(', ') || '');
        }
      } catch (error) {
        console.error('Error fetching request data:', error);
        setMessage('Invalid or expired link. Please request a new link.');
      } finally {
        setLoadingRequest(false);
      }
    };

    fetchRequestData();
  }, [reqId, token]);

  // if logged in, log out first to set password
  if (currentUser) {
    AuthService.logout();
    navigate(0);
  }

  if (!reqId || !token) {
	return (<div id="login-container">
		<div className="container">
			<div className="col-md-12">
			<div id="login-content"><div className="alert alert-danger" role="alert">{t('trial_env.invalid_link')}</div></div>
		</div>
		</div>
	</div>);
  }

  if (loadingRequest) {
    return (
      <div id="login-container">
        <div className="container">
          <div className="col-md-12">
            <div id="login-content">
              <div className="text-center">
                <span className="spinner-border spinner-border-lg" />
                <p>{t('loading')}...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }


  const onChangePasswordAgain = (e) => {
    setPasswordAgain(e.target.value);
  };

  const onChangePassword = (e) => {
    setPassword(e.target.value);
  };

  const showTerms = (e) => {
	e.preventDefault();
	openModal(MODAL_TYPES.INFO, { content: getTermsContent(i18n.language), title: t('trial_env.terms_title'), isMarkdown: true, modalSize: 'xl' });
  };

  const handleCreateEnvironment = async (e) => {
    e.preventDefault();

	try {
		setMessage("");
		setLoading(true);
	
		if(!isPasswordValid) {
			setMessage(t('trial_env.password_requirements_error'));
			setLoading(false);
			return;
		}
		

		// For KNMT users, only send password (name/org/project already in request)
		const response = isKNMT 
			? await AuthService.createTrialEnvironment(reqId, token, null, password, null, null)
			: await AuthService.createTrialEnvironment(reqId, token, name, password, orgName, projectName);

		if(response.status !== 200) {
			const msg = response.data.msgKey ? t(response.data.msgKey) : response.data.msg;
			setMessage(msg);
			setLoading(false);
			return;
		}

		const successMsg = response.data.msgKey ? t(response.data.msgKey) : response.data.msg;
		setMessage(successMsg);
		if (response.data.environmentUrl) {
			setEnvironmentUrl(response.data.environmentUrl);
		}
		setLoading(false);
	
	} catch (error) {
		const errorMsg = error.response?.data?.msgKey ? t(error.response.data.msgKey) : error.response?.data?.msg;
		setMessage(errorMsg);
		setLoading(false);
	}
  };

  return (
    <div id="login-container">
      <div className="container">
        <div className="col-md-12">
          <div id="login-content">


              <div className="card card-container">
              <LanguageSwitcher />
              <div className="profile-img-card" />
              <h1>{t('trial_env.title')}</h1>
              {isKNMT ? (
                <>
                  <h3>{t('trial_env.knmt_welcome')}</h3>
                  <p>{t('trial_env.knmt_description')}</p>
                </>
              ) : (
                <h3>{t('trial_env.description')}</h3>
              )}

              <hr />              {message && (
                <div className="form-group">
                  <div className={"alert alert-" + (message.includes("succes") ? "success" : "danger")} role="alert" style={{ whiteSpace: 'pre-line' }}>
                    {message}
                  </div>

				  {environmentUrl && message.includes("succes") && (
                      <div style={{ marginTop: '15px', textAlign: 'center' }}>
                        <a href={`${environmentUrl}?language=${i18n.language || 'en'}`} style={{ textDecoration: 'underline'}}>
                          {t('trial_env.go_to_login')}
                        </a>
                      </div>
                    )}
                </div>
              )}
			{!message.includes("succes") && (
					
				<form onSubmit={handleCreateEnvironment}>

					{isKNMT && (
						<>
							<div className="form-group">
								<label htmlFor="email">{t('trial_env.email')}</label>
								<input type="email" className="form-control" name="email" value={requestData?.email || ''} disabled />
								<small className="form-text text-muted">{t('trial_env.auto_filled')}</small>
							</div>

							<div className="form-group">
								<label htmlFor="name">{t('trial_env.practice_owner_name')}</label>
								<input type="input" className="form-control" name="name" value={name} disabled />
								<small className="form-text text-muted">{t('trial_env.auto_filled')}</small>
							</div>

							<div className="form-group">
								<label htmlFor="orgName">{t('trial_env.practice_name')}</label>
								<input type="input" className="form-control" name="orgName" value={orgName} disabled />
								<small className="form-text text-muted">{t('trial_env.auto_filled')}</small>
							</div>

							<div className="form-group">
								<label htmlFor="projectName">{t('trial_env.locations')}</label>
								<input type="input" className="form-control" name="projectName" value={projectName} disabled />
								<small className="form-text text-muted">{t('trial_env.auto_filled')}</small>
							</div>
						</>
					)}

					{!isKNMT && (
						<div className="form-group">
							<label htmlFor="name">{t('name')}</label>
							<input type="input" className="form-control" name="name" placeholder="John Doe" value={name} onChange={(e) => setName(e.target.value)} required />
						</div>
					)}

					<div className="form-group">
					<label htmlFor="password">{t('trial_env.password')}</label>
					<input
						type="password"
						className="form-control"
						name="password"
						placeholder="*********"
						value={password}
						onChange={onChangePassword}
						required
					/>
					</div>

					<div className="form-group">
					<label htmlFor="password">{t('trial_env.password_repeat')}</label>
					<input
						type="password"
						className="form-control"
						name="passwordAgain"
						placeholder="*********"
						value={passwordAgain}
						onChange={onChangePasswordAgain}
						required
					/>
					</div>

					{(password || passwordAgain) && (
						<div style={{marginTop: 20}}>
							<PasswordChecklist
								rules={[
								"minLength",
								"specialChar",
								"number",
								"capital",
								"lowercase",
								"match",
								]}
								minLength={12}
								value={password}
								valueAgain={passwordAgain}
								onChange={(isValid) => {
								setIsPasswordValid(isValid);
								}}
								messages={{
									minLength: t('password_min_length'),
									specialChar: t('password_special_char'),
									number: t('password_number'),
									capital: t('password_capital'),
									lowercase: t('password_lowercase'),
									match: t('password_match')
								}}
							/>
						</div>
					)}

					{!isKNMT && (
						<>
							<div className="form-group">
								<label htmlFor="orgName">{t('trial_env.organization_name')}</label>
								<input type="input" className="form-control" name="orgName" placeholder={t('trial_env.organization_placeholder')} value={orgName} onChange={(e) => setOrgName(e.target.value)} required />
							</div>

							<div className="form-group">
								<label htmlFor="projectName">{t('trial_env.project_name')}</label>
								<input type="input" className="form-control" name="projectName" placeholder={t('trial_env.project_placeholder')} value={projectName} onChange={(e) => setProjectName(e.target.value)} required />
							</div>
						</>
					)}

					
					<div className="form-check">
						<input type="checkbox" className="form-check-input" id="agree" required />
						<label className="form-check-label" htmlFor="agree">{t('trial_env.agree_terms')} <a href="#" onClick={showTerms} className="underline" target="_blank">{t('trial_env.terms_link')}</a></label>
					</div>
					

					<div className="form-group">
						<button className="btn btn-outline-primary btn-block login-submit" disabled={loading} >
							{loading && (
							<span className="spinner-border spinner-border-sm" />
							)}
							<span>{t('trial_env.setup_button')}</span>
						</button>
					</div>

				</form>
				)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTrialEnvironment;
